import { apiRequest, safeJson } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export type UrgencyLevel = "critical" | "high" | "elevated" | "moderate" | "low" | "none";
export type RiskLevel = "critical" | "high" | "elevated" | "moderate" | "low";
export type ImpactWeight = "extreme" | "high" | "medium" | "low" | "minimal";

export type HookUrgencyLevel = 1 | 2 | 3 | 4 | 5;
export type ImpactTier = "low" | "medium" | "high" | "critical";
export type RiskTier = "safe" | "watch" | "elevated" | "high" | "critical";

export const URGENCY_TOKENS = {
  1: "--urgency-1",
  2: "--urgency-2",
  3: "--urgency-3",
  4: "--urgency-4",
  5: "--urgency-5",
} as const;

export const IMPACT_TOKENS = {
  low: "--impact-low",
  medium: "--impact-medium",
  high: "--impact-high",
  critical: "--impact-critical",
} as const;

export const RISK_TOKENS = {
  safe: "--risk-safe",
  watch: "--risk-watch",
  elevated: "--risk-elevated",
  high: "--risk-high",
  critical: "--risk-critical",
} as const;

export const ANOMALY_TOKEN = "--anomaly" as const;

export const URGENCY_CLASSES = {
  1: { text: "text-urgency-1", bg: "bg-urgency-1", border: "border-urgency-1" },
  2: { text: "text-urgency-2", bg: "bg-urgency-2", border: "border-urgency-2" },
  3: { text: "text-urgency-3", bg: "bg-urgency-3", border: "border-urgency-3" },
  4: { text: "text-urgency-4", bg: "bg-urgency-4", border: "border-urgency-4" },
  5: { text: "text-urgency-5", bg: "bg-urgency-5", border: "border-urgency-5" },
} as const;

export const IMPACT_CLASSES = {
  low: { text: "text-impact-low", bg: "bg-impact-low", border: "border-impact-low" },
  medium: { text: "text-impact-medium", bg: "bg-impact-medium", border: "border-impact-medium" },
  high: { text: "text-impact-high", bg: "bg-impact-high", border: "border-impact-high" },
  critical: { text: "text-impact-critical", bg: "bg-impact-critical", border: "border-impact-critical" },
} as const;

export const RISK_CLASSES = {
  safe: { text: "text-risk-safe", bg: "bg-risk-safe", border: "border-risk-safe" },
  watch: { text: "text-risk-watch", bg: "bg-risk-watch", border: "border-risk-watch" },
  elevated: { text: "text-risk-elevated", bg: "bg-risk-elevated", border: "border-risk-elevated" },
  high: { text: "text-risk-high", bg: "bg-risk-high", border: "border-risk-high" },
  critical: { text: "text-risk-critical", bg: "bg-risk-critical", border: "border-risk-critical" },
} as const;

export interface TemporalIntensityConfig {
  level5Threshold: number;
  level4Threshold: number;
  level3Threshold: number;
  level2Threshold: number;
}

const DEFAULT_THRESHOLDS: TemporalIntensityConfig = {
  level5Threshold: -1,
  level4Threshold: 1,
  level3Threshold: 3,
  level2Threshold: 7,
};

export interface TemporalIntensityHookResult {
  level: HookUrgencyLevel;
  daysUntilDue: number;
  isOverdue: boolean;
  token: string;
  classes: { text: string; bg: string; border: string; };
}

export function useTemporalIntensity(
  dueDate: string | Date | null | undefined,
  status?: string,
  config?: Partial<TemporalIntensityConfig>,
): TemporalIntensityHookResult {
  return useMemo(() => {
    if (!dueDate || status === "paid" || status === "cancelled" || status === "void") {
      return {
        level: 1 as HookUrgencyLevel,
        daysUntilDue: Infinity,
        isOverdue: false,
        token: URGENCY_TOKENS[1],
        classes: URGENCY_CLASSES[1],
      };
    }

    const thresholds = { ...DEFAULT_THRESHOLDS, ...config };
    const due = typeof dueDate === "string" ? new Date(dueDate) : dueDate;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const dueNorm = new Date(due);
    dueNorm.setHours(0, 0, 0, 0);
    const diffMs = dueNorm.getTime() - now.getTime();
    const daysUntilDue = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    const isOverdue = daysUntilDue < 0;

    let level: HookUrgencyLevel;
    if (daysUntilDue <= thresholds.level5Threshold || status === "overdue") {
      level = 5;
    } else if (daysUntilDue <= thresholds.level4Threshold) {
      level = 4;
    } else if (daysUntilDue <= thresholds.level3Threshold) {
      level = 3;
    } else if (daysUntilDue <= thresholds.level2Threshold) {
      level = 2;
    } else {
      level = 1;
    }

    return {
      level,
      daysUntilDue,
      isOverdue,
      token: URGENCY_TOKENS[level],
      classes: URGENCY_CLASSES[level],
    };
  }, [dueDate, status, config]);
}

export interface CashImpactResult {
  tier: ImpactTier;
  ratio: number;
  token: string;
  classes: { text: string; bg: string; border: string; };
}

const DEFAULT_CONTEXT_MAX = 100000;

export function useCashImpactWeight(
  amount: number | string | null | undefined,
  contextMax?: number | null,
): CashImpactResult {
  return useMemo(() => {
    const parsed = typeof amount === "string" ? parseFloat(amount) : (amount ?? 0);
    const numAmount = Number.isFinite(parsed) ? parsed : 0;
    const max = contextMax && Number.isFinite(contextMax) && contextMax > 0 ? contextMax : DEFAULT_CONTEXT_MAX;
    const absAmount = Math.abs(numAmount);
    const ratio = max > 0 ? absAmount / max : 0;

    let tier: ImpactTier;
    if (ratio >= 0.75) {
      tier = "critical";
    } else if (ratio >= 0.5) {
      tier = "high";
    } else if (ratio >= 0.25) {
      tier = "medium";
    } else {
      tier = "low";
    }

    return {
      tier,
      ratio,
      token: IMPACT_TOKENS[tier],
      classes: IMPACT_CLASSES[tier],
    };
  }, [amount, contextMax]);
}

export interface RiskScoreResult {
  tier: RiskTier;
  score: number;
  onTimeRate: number;
  avgDaysLate: number;
  token: string;
  classes: { text: string; bg: string; border: string; };
  isLoading: boolean;
  /**
   * The outstanding-items read failed, so `tier` was computed from no data
   * and means "unknown", not "healthy". Callers must not render a reassuring
   * badge on this.
   */
  isError: boolean;
}

interface OutstandingItem {
  dueDate?: string | null;
  status?: string;
  amountDue?: number | string | null;
}

function computeRiskFromOutstanding(
  items: OutstandingItem[],
): { tier: RiskTier; score: number; onTimeRate: number; avgDaysLate: number; } {
  if (!items || items.length === 0) {
    return { tier: "safe", score: 100, onTimeRate: 1, avgDaysLate: 0 };
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  let onTimeCount = 0;
  let totalDaysLate = 0;
  let itemCount = 0;

  for (const item of items) {
    if (!item.dueDate) continue;
    itemCount++;
    const due = new Date(item.dueDate);
    due.setHours(0, 0, 0, 0);
    const daysLate = Math.max(0, Math.ceil((now.getTime() - due.getTime()) / (1000 * 60 * 60 * 24)));
    if (daysLate <= 0) {
      onTimeCount++;
    } else {
      totalDaysLate += daysLate;
    }
  }

  const onTimeRate = itemCount > 0 ? onTimeCount / itemCount : 1;
  const lateCount = itemCount - onTimeCount;
  const avgDaysLate = lateCount > 0 ? totalDaysLate / lateCount : 0;

  const score = Math.round(onTimeRate * 70 + Math.max(0, 30 - avgDaysLate));

  let tier: RiskTier;
  if (score >= 90) {
    tier = "safe";
  } else if (score >= 75) {
    tier = "watch";
  } else if (score >= 55) {
    tier = "elevated";
  } else if (score >= 35) {
    tier = "high";
  } else {
    tier = "critical";
  }

  return { tier, score, onTimeRate, avgDaysLate };
}

export function getTemporalUrgency(
  dueDate: string | Date | null | undefined,
  status?: string,
  config?: Partial<TemporalIntensityConfig>,
): TemporalIntensityHookResult {
  if (!dueDate || status === "paid" || status === "cancelled" || status === "void") {
    return {
      level: 1,
      daysUntilDue: Infinity,
      isOverdue: false,
      token: URGENCY_TOKENS[1],
      classes: URGENCY_CLASSES[1],
    };
  }
  const thresholds = { ...DEFAULT_THRESHOLDS, ...config };
  const due = typeof dueDate === "string" ? new Date(dueDate) : dueDate;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const dueNorm = new Date(due);
  dueNorm.setHours(0, 0, 0, 0);
  const diffMs = dueNorm.getTime() - now.getTime();
  const daysUntilDue = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  const isOverdue = daysUntilDue < 0;
  let level: HookUrgencyLevel;
  if (daysUntilDue <= thresholds.level5Threshold || status === "overdue") level = 5;
  else if (daysUntilDue <= thresholds.level4Threshold) level = 4;
  else if (daysUntilDue <= thresholds.level3Threshold) level = 3;
  else if (daysUntilDue <= thresholds.level2Threshold) level = 2;
  else level = 1;
  return { level, daysUntilDue, isOverdue, token: URGENCY_TOKENS[level], classes: URGENCY_CLASSES[level] };
}

export function getCashImpactWeight(
  amount: number | string | null | undefined,
  contextMax?: number | null,
): CashImpactResult {
  const parsed = typeof amount === "string" ? parseFloat(amount) : (amount ?? 0);
  const numAmount = Number.isFinite(parsed) ? parsed : 0;
  const max = contextMax && Number.isFinite(contextMax) && contextMax > 0 ? contextMax : DEFAULT_CONTEXT_MAX;
  const absAmount = Math.abs(numAmount);
  const ratio = max > 0 ? absAmount / max : 0;
  let tier: ImpactTier;
  if (ratio >= 0.75) tier = "critical";
  else if (ratio >= 0.5) tier = "high";
  else if (ratio >= 0.25) tier = "medium";
  else tier = "low";
  return { tier, ratio, token: IMPACT_TOKENS[tier], classes: IMPACT_CLASSES[tier] };
}

export type UtilizationTier = "safe" | "moderate" | "elevated" | "high" | "critical";

export const UTILIZATION_CLASSES = {
  safe: { text: "text-urgency-1", bg: "bg-urgency-1", bar: "bg-urgency-1" },
  moderate: { text: "text-urgency-2", bg: "bg-urgency-2", bar: "bg-urgency-2" },
  elevated: { text: "text-urgency-3", bg: "bg-urgency-3", bar: "bg-urgency-3" },
  high: { text: "text-urgency-4", bg: "bg-urgency-4", bar: "bg-urgency-4" },
  critical: { text: "text-urgency-5", bg: "bg-urgency-5", bar: "bg-urgency-5" },
} as const;

export function getUtilizationTier(
  percent: number,
): { tier: UtilizationTier; classes: typeof UTILIZATION_CLASSES[UtilizationTier]; } {
  if (percent >= 95) return { tier: "critical", classes: UTILIZATION_CLASSES.critical };
  if (percent >= 80) return { tier: "high", classes: UTILIZATION_CLASSES.high };
  if (percent >= 60) return { tier: "elevated", classes: UTILIZATION_CLASSES.elevated };
  if (percent >= 40) return { tier: "moderate", classes: UTILIZATION_CLASSES.moderate };
  return { tier: "safe", classes: UTILIZATION_CLASSES.safe };
}

export type RunHealthTier = "healthy" | "degraded" | "warning" | "failing" | "critical";

export const RUN_HEALTH_CLASSES = {
  healthy: { text: "text-urgency-1", bg: "bg-urgency-1/15", border: "border-urgency-1" },
  degraded: { text: "text-urgency-2", bg: "bg-urgency-2/15", border: "border-urgency-2" },
  warning: { text: "text-urgency-3", bg: "bg-urgency-3/15", border: "border-urgency-3" },
  failing: { text: "text-urgency-4", bg: "bg-urgency-4/15", border: "border-urgency-4" },
  critical: { text: "text-urgency-5", bg: "bg-urgency-5/15", border: "border-urgency-5" },
} as const;

export function getRunHealthTier(
  successRate: number,
): { tier: RunHealthTier; classes: typeof RUN_HEALTH_CLASSES[RunHealthTier]; } {
  if (successRate >= 95) return { tier: "healthy", classes: RUN_HEALTH_CLASSES.healthy };
  if (successRate >= 80) return { tier: "degraded", classes: RUN_HEALTH_CLASSES.degraded };
  if (successRate >= 60) return { tier: "warning", classes: RUN_HEALTH_CLASSES.warning };
  if (successRate >= 30) return { tier: "failing", classes: RUN_HEALTH_CLASSES.failing };
  return { tier: "critical", classes: RUN_HEALTH_CLASSES.critical };
}

export function getFrequencyIntensity(firesPerDay: number): HookUrgencyLevel {
  if (firesPerDay >= 500) return 5;
  if (firesPerDay >= 100) return 4;
  if (firesPerDay >= 50) return 3;
  if (firesPerDay >= 10) return 2;
  return 1;
}

export function getAgingUrgencyLevel(daysOverdue: number): HookUrgencyLevel {
  if (daysOverdue >= 90) return 5;
  if (daysOverdue >= 60) return 4;
  if (daysOverdue >= 30) return 3;
  if (daysOverdue >= 1) return 2;
  return 1;
}

export const WARM_URGENCY_CLASSES = {
  1: { text: "text-foreground", bg: "bg-muted", border: "border-border" },
  2: {
    text: "text-amber-700 dark:text-amber-400",
    bg: "bg-amber-50 dark:bg-amber-950/30",
    border: "border-amber-200 dark:border-amber-800",
  },
  3: {
    text: "text-amber-800 dark:text-amber-300",
    bg: "bg-amber-100/70 dark:bg-amber-900/30",
    border: "border-amber-300 dark:border-amber-700",
  },
  4: {
    text: "text-orange-700 dark:text-orange-400",
    bg: "bg-orange-50 dark:bg-orange-950/30",
    border: "border-orange-200 dark:border-orange-800",
  },
  5: {
    text: "text-orange-800 dark:text-orange-300",
    bg: "bg-orange-100/70 dark:bg-orange-900/30",
    border: "border-orange-300 dark:border-orange-700",
  },
} as const;

export function getWarmUrgency(
  daysUntilDue: number,
): { level: HookUrgencyLevel; classes: typeof WARM_URGENCY_CLASSES[HookUrgencyLevel]; } {
  let level: HookUrgencyLevel;
  if (daysUntilDue < 0) level = Math.abs(daysUntilDue) > 14 ? 5 : 4;
  else if (daysUntilDue === 0) level = 3;
  else if (daysUntilDue <= 7) level = 2;
  else level = 1;
  return { level, classes: WARM_URGENCY_CLASSES[level] };
}

export const SYNC_HEALTH_CLASSES = {
  healthy: { text: "text-synced", bg: "bg-synced/15", border: "border-synced", dot: "bg-synced" },
  degraded: { text: "text-urgency-2", bg: "bg-urgency-2/15", border: "border-urgency-2", dot: "bg-urgency-2" },
  warning: { text: "text-urgency-3", bg: "bg-urgency-3/15", border: "border-urgency-3", dot: "bg-urgency-3" },
  failing: { text: "text-urgency-4", bg: "bg-urgency-4/15", border: "border-urgency-4", dot: "bg-urgency-4" },
  critical: { text: "text-urgency-5", bg: "bg-urgency-5/15", border: "border-urgency-5", dot: "bg-urgency-5" },
} as const;

export type SyncHealthTier = "healthy" | "degraded" | "warning" | "failing" | "critical";

export function getSyncHealthTier(
  errorCount: number,
  conflictCount: number,
  syncedCount: number,
): { tier: SyncHealthTier; classes: typeof SYNC_HEALTH_CLASSES[SyncHealthTier]; } {
  const total = errorCount + conflictCount + syncedCount;
  if (total === 0) return { tier: "healthy", classes: SYNC_HEALTH_CLASSES.healthy };
  const errorRate = total > 0 ? errorCount / total : 0;
  const conflictRate = total > 0 ? conflictCount / total : 0;
  if (errorRate >= 0.5) return { tier: "critical", classes: SYNC_HEALTH_CLASSES.critical };
  if (errorRate >= 0.2) return { tier: "failing", classes: SYNC_HEALTH_CLASSES.failing };
  if (errorRate > 0 || conflictRate >= 0.3) return { tier: "warning", classes: SYNC_HEALTH_CLASSES.warning };
  if (conflictRate > 0) return { tier: "degraded", classes: SYNC_HEALTH_CLASSES.degraded };
  return { tier: "healthy", classes: SYNC_HEALTH_CLASSES.healthy };
}

export function useRiskScore(
  entityId: string | null | undefined,
  entityType: "customer" | "vendor" = "customer",
): RiskScoreResult {
  const endpoint = entityType === "customer"
    ? `/api/customers/${entityId}/outstanding-invoices`
    : `/api/vendors/${entityId}/bills`;

  const { data: items, isLoading, isError } = useQuery<OutstandingItem[]>({
    queryKey: [entityType === "customer" ? "/api/customers" : "/api/vendors", entityId, "outstanding"],
    enabled: !!entityId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      // OCT-10873: `apiRequest` is what attaches `X-Octa-Entity-Id`, and
      // `GET /api/vendors/:id/bills` deliberately fails closed on a
      // header-less read - it answers 400 rather than an empty list, because
      // one vendor's bill list has no useful "empty" reading when the caller
      // named no entity. A bare `fetch` sent no header, so this read was a
      // guaranteed 400, and `if (!res.ok) return []` swallowed it into a risk
      // tier computed from zero bills - which reads as "healthy".
      //
      // Note on blast radius: `useRiskScore` currently has NO callers, so
      // that wrong badge was not reachable in the running app. The defect was
      // real, the user-visible symptom was not.
      //
      // The swallow is dropped rather than kept so the tier is never computed
      // from a failed read, and `isError` is surfaced below so the first real
      // caller can tell "no outstanding items" from "could not find out".
      const res = await apiRequest("GET", endpoint);
      const data = await safeJson<
        OutstandingItem[] | { data?: OutstandingItem[]; invoices?: OutstandingItem[]; bills?: OutstandingItem[]; }
      >(res);
      if (Array.isArray(data)) return data;
      return data?.data || data?.invoices || data?.bills || [];
    },
  });

  return useMemo(() => {
    const result = computeRiskFromOutstanding(items || []);
    return {
      ...result,
      token: RISK_TOKENS[result.tier],
      classes: RISK_CLASSES[result.tier],
      isLoading,
      isError,
    };
  }, [items, isLoading, isError]);
}

export interface TemporalIntensity {
  level: UrgencyLevel;
  daysUntilDue: number;
  className: string;
  borderClassName: string;
  dotClassName: string;
  pulseClassName: string;
}

export interface ImpactResult {
  weight: ImpactWeight;
  className: string;
  textClassName: string;
}

export interface RiskGradientResult {
  level: RiskLevel;
  className: string;
  bgClassName: string;
  textClassName: string;
  dotClassName: string;
  label: string;
}

export interface PriorityScoreResult {
  score: number;
  urgencyComponent: number;
  impactComponent: number;
  riskComponent: number;
}

export function getTemporalIntensity(
  dueDate: string | null | undefined,
  status?: string,
): TemporalIntensity {
  const terminalStatuses = ["paid", "cancelled", "void", "write_off"];
  if (!dueDate || (status && terminalStatuses.includes(status))) {
    return {
      level: "none",
      daysUntilDue: 999,
      className: "",
      borderClassName: "",
      dotClassName: "bg-muted-foreground/30",
      pulseClassName: "",
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + "T00:00:00");
  const diffMs = due.getTime() - today.getTime();
  const daysUntilDue = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (daysUntilDue < -30) {
    return {
      level: "critical",
      daysUntilDue,
      className: "bg-red-500/10 dark:bg-red-500/15",
      borderClassName: "border-red-500/40",
      dotClassName: "bg-red-500",
      pulseClassName: "animate-pulse",
    };
  }
  if (daysUntilDue < -7) {
    return {
      level: "high",
      daysUntilDue,
      className: "bg-red-500/8 dark:bg-red-500/10",
      borderClassName: "border-red-400/30",
      dotClassName: "bg-red-400",
      pulseClassName: "",
    };
  }
  if (daysUntilDue < 0) {
    return {
      level: "elevated",
      daysUntilDue,
      className: "bg-orange-500/8 dark:bg-orange-500/10",
      borderClassName: "border-orange-400/30",
      dotClassName: "bg-orange-400",
      pulseClassName: "",
    };
  }
  if (daysUntilDue <= 3) {
    return {
      level: "moderate",
      daysUntilDue,
      className: "bg-amber-500/6 dark:bg-amber-500/8",
      borderClassName: "border-amber-400/25",
      dotClassName: "bg-amber-400",
      pulseClassName: "",
    };
  }
  if (daysUntilDue <= 7) {
    return {
      level: "low",
      daysUntilDue,
      className: "",
      borderClassName: "border-amber-300/15",
      dotClassName: "bg-amber-300",
      pulseClassName: "",
    };
  }
  return {
    level: "none",
    daysUntilDue,
    className: "",
    borderClassName: "",
    dotClassName: "bg-muted-foreground/30",
    pulseClassName: "",
  };
}

export function getImpactWeight(amount: number, maxAmount?: number): ImpactResult {
  const threshold = maxAmount || 100000;
  const ratio = Math.min(amount / threshold, 1);

  if (ratio >= 0.8 || amount >= 500000) {
    return {
      weight: "extreme",
      className: "font-bold",
      textClassName: "text-foreground font-bold",
    };
  }
  if (ratio >= 0.5 || amount >= 100000) {
    return {
      weight: "high",
      className: "font-semibold",
      textClassName: "text-foreground font-semibold",
    };
  }
  if (ratio >= 0.25 || amount >= 25000) {
    return {
      weight: "medium",
      className: "font-medium",
      textClassName: "text-foreground font-medium",
    };
  }
  if (ratio >= 0.1 || amount >= 5000) {
    return {
      weight: "low",
      className: "",
      textClassName: "text-foreground",
    };
  }
  return {
    weight: "minimal",
    className: "",
    textClassName: "text-muted-foreground",
  };
}

export function getRiskGradient(score: number): RiskGradientResult {
  if (score < 20) {
    return {
      level: "critical",
      className: "bg-red-500",
      bgClassName: "bg-red-500/10 dark:bg-red-500/15",
      textClassName: "text-red-600 dark:text-red-400",
      dotClassName: "bg-red-500",
      label: "Critical",
    };
  }
  if (score < 40) {
    return {
      level: "high",
      className: "bg-orange-500",
      bgClassName: "bg-orange-500/10 dark:bg-orange-500/12",
      textClassName: "text-orange-600 dark:text-orange-400",
      dotClassName: "bg-orange-500",
      label: "High Risk",
    };
  }
  if (score < 60) {
    return {
      level: "elevated",
      className: "bg-amber-500",
      bgClassName: "bg-amber-500/8 dark:bg-amber-500/10",
      textClassName: "text-amber-600 dark:text-amber-400",
      dotClassName: "bg-amber-500",
      label: "Elevated",
    };
  }
  if (score < 80) {
    return {
      level: "moderate",
      className: "bg-emerald-400",
      bgClassName: "bg-emerald-500/6 dark:bg-emerald-500/8",
      textClassName: "text-emerald-600 dark:text-emerald-400",
      dotClassName: "bg-emerald-400",
      label: "Moderate",
    };
  }
  return {
    level: "low",
    className: "bg-emerald-500",
    bgClassName: "bg-emerald-500/8 dark:bg-emerald-500/10",
    textClassName: "text-emerald-600 dark:text-emerald-400",
    dotClassName: "bg-emerald-500",
    label: "Good",
  };
}

export function getPriorityScore(
  daysUntilDue: number,
  amount: number,
  riskScore?: number,
): PriorityScoreResult {
  let urgencyComponent: number;
  if (daysUntilDue < -30) urgencyComponent = 100;
  else if (daysUntilDue < -7) urgencyComponent = 85;
  else if (daysUntilDue < 0) urgencyComponent = 70;
  else if (daysUntilDue <= 3) urgencyComponent = 55;
  else if (daysUntilDue <= 7) urgencyComponent = 35;
  else urgencyComponent = 10;

  let impactComponent: number;
  if (amount >= 500000) impactComponent = 100;
  else if (amount >= 100000) impactComponent = 80;
  else if (amount >= 25000) impactComponent = 60;
  else if (amount >= 5000) impactComponent = 40;
  else if (amount >= 1000) impactComponent = 20;
  else impactComponent = 5;

  const riskComponent = riskScore != null ? Math.max(0, 100 - riskScore) : 50;

  const score = urgencyComponent * 0.45 + impactComponent * 0.30 + riskComponent * 0.25;

  return {
    score: Math.round(score),
    urgencyComponent,
    impactComponent,
    riskComponent,
  };
}

export function getDiscountUrgency(daysLeft: number | null): {
  level: "expired" | "urgent" | "warning" | "calm" | "none";
  className: string;
  badgeClassName: string;
} {
  if (daysLeft === null) {
    return { level: "none", className: "", badgeClassName: "" };
  }
  if (daysLeft <= 0) {
    return {
      level: "expired",
      className: "line-through opacity-60",
      badgeClassName: "bg-muted text-muted-foreground line-through",
    };
  }
  if (daysLeft <= 2) {
    return {
      level: "urgent",
      className: "text-red-600 dark:text-red-400 font-medium",
      badgeClassName: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-400/30 animate-pulse",
    };
  }
  if (daysLeft <= 5) {
    return {
      level: "warning",
      className: "text-amber-600 dark:text-amber-400 font-medium",
      badgeClassName: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-400/30",
    };
  }
  return {
    level: "calm",
    className: "text-muted-foreground",
    badgeClassName: "bg-emerald-500/8 text-emerald-600 dark:text-emerald-400 border-emerald-400/20",
  };
}

export function getSyncHealthComposite(
  errorCount: number,
  conflictCount: number,
  syncedCount: number,
  lastSyncedAt?: string | null,
  avgLatencyMs?: number | null,
): { tier: SyncHealthTier; classes: typeof SYNC_HEALTH_CLASSES[SyncHealthTier]; freshnessMinutes: number; } {
  const total = errorCount + conflictCount + syncedCount;
  const errorRate = total > 0 ? errorCount / total : 0;
  const conflictRate = total > 0 ? conflictCount / total : 0;

  let freshnessMinutes = 0;
  if (lastSyncedAt) {
    freshnessMinutes = Math.floor((Date.now() - new Date(lastSyncedAt).getTime()) / 60000);
  }

  let baseScore = 100;
  if (errorRate >= 0.5) baseScore -= 60;
  else if (errorRate >= 0.2) baseScore -= 40;
  else if (errorRate > 0) baseScore -= 20;
  if (conflictRate >= 0.3) baseScore -= 20;
  else if (conflictRate > 0) baseScore -= 10;

  if (freshnessMinutes > 1440) baseScore -= 15;
  else if (freshnessMinutes > 360) baseScore -= 8;
  else if (freshnessMinutes > 60) baseScore -= 3;

  if (avgLatencyMs && avgLatencyMs > 10000) baseScore -= 10;
  else if (avgLatencyMs && avgLatencyMs > 5000) baseScore -= 5;

  let tier: SyncHealthTier;
  if (baseScore <= 20) tier = "critical";
  else if (baseScore <= 40) tier = "failing";
  else if (baseScore <= 60) tier = "warning";
  else if (baseScore <= 80) tier = "degraded";
  else tier = "healthy";

  return { tier, classes: SYNC_HEALTH_CLASSES[tier], freshnessMinutes };
}

export function getRetryAgeUrgency(errorTimestamp: string | null | undefined): HookUrgencyLevel {
  if (!errorTimestamp) return 3;
  const ageMinutes = Math.floor((Date.now() - new Date(errorTimestamp).getTime()) / 60000);
  if (ageMinutes > 1440) return 5;
  if (ageMinutes > 360) return 4;
  if (ageMinutes > 60) return 3;
  if (ageMinutes > 15) return 2;
  return 1;
}

export function getFailureRecencyUrgency(lastFailedAt: string | null | undefined): HookUrgencyLevel {
  if (!lastFailedAt) return 1;
  const ageMs = Date.now() - new Date(lastFailedAt).getTime();
  const ageHours = ageMs / (1000 * 60 * 60);
  if (ageHours < 1) return 5;
  if (ageHours < 6) return 4;
  if (ageHours < 24) return 3;
  if (ageHours < 72) return 2;
  return 1;
}

export function getAnomalyLevel(
  currentValue: number,
  previousValue: number,
): { level: HookUrgencyLevel; changePercent: number; isAnomaly: boolean; } {
  if (previousValue === 0) {
    return { level: 1, changePercent: 0, isAnomaly: false };
  }
  const changePercent = ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
  const absChange = Math.abs(changePercent);
  if (absChange >= 50) return { level: 5, changePercent, isAnomaly: true };
  if (absChange >= 30) return { level: 4, changePercent, isAnomaly: true };
  if (absChange >= 15) return { level: 3, changePercent, isAnomaly: true };
  if (absChange >= 5) return { level: 2, changePercent, isAnomaly: false };
  return { level: 1, changePercent, isAnomaly: false };
}

export function getOutstandingImpactTier(totalOutstanding: number, invoiceCount: number): {
  tier: ImpactTier;
  classes: typeof IMPACT_CLASSES[ImpactTier];
  perInvoiceAvg: number;
} {
  const perInvoiceAvg = invoiceCount > 0 ? totalOutstanding / invoiceCount : 0;
  let tier: ImpactTier;
  if (totalOutstanding >= 100000 || perInvoiceAvg >= 25000) tier = "critical";
  else if (totalOutstanding >= 50000 || perInvoiceAvg >= 10000) tier = "high";
  else if (totalOutstanding >= 10000 || perInvoiceAvg >= 2500) tier = "medium";
  else tier = "low";
  return { tier, classes: IMPACT_CLASSES[tier], perInvoiceAvg };
}

export type ToastUrgencyVariant =
  | "default"
  | "destructive"
  | "warning"
  | "urgency1"
  | "urgency2"
  | "urgency3"
  | "urgency4"
  | "urgency5";

export function getToastUrgencyVariant(
  severity: "info" | "warning" | "error" | "critical" | "success",
): ToastUrgencyVariant {
  switch (severity) {
    case "critical":
      return "urgency5";
    case "error":
      return "urgency4";
    case "warning":
      return "urgency3";
    case "info":
      return "urgency2";
    case "success":
      return "urgency1";
    default:
      return "default";
  }
}

export function getChatCellUrgency(headerText: string, cellValue: string): HookUrgencyLevel | null {
  const header = headerText.toLowerCase();
  const numVal = parseFloat(cellValue.replace(/[^0-9.\-]/g, ""));

  if (header.includes("overdue") || header.includes("days late") || header.includes("aging")) {
    if (!isNaN(numVal)) return getAgingUrgencyLevel(numVal);
  }
  if (header.includes("risk") || header.includes("score")) {
    if (!isNaN(numVal)) {
      if (numVal <= 20) return 5;
      if (numVal <= 40) return 4;
      if (numVal <= 60) return 3;
      if (numVal <= 80) return 2;
      return 1;
    }
  }
  if (header.includes("status")) {
    const lower = cellValue.toLowerCase();
    if (lower === "overdue" || lower === "critical" || lower === "failed") return 5;
    if (lower === "past due" || lower === "high") return 4;
    if (lower === "pending" || lower === "warning") return 3;
  }
  if (header.includes("amount") || header.includes("balance") || header.includes("total")) {
    if (!isNaN(numVal)) {
      if (numVal >= 100000) return 5;
      if (numVal >= 50000) return 4;
      if (numVal >= 10000) return 3;
    }
  }
  return null;
}

export function getEntityHealthLevel(
  overdueInvoices: number,
  overdueBills: number,
  syncErrors: number,
): RiskGradientResult {
  const problems = overdueInvoices * 3 + overdueBills * 2 + syncErrors * 4;
  if (problems === 0) return getRiskGradient(95);
  if (problems <= 3) return getRiskGradient(75);
  if (problems <= 8) return getRiskGradient(55);
  if (problems <= 15) return getRiskGradient(35);
  return getRiskGradient(15);
}

export function getUrgencyBorderClass(level: UrgencyLevel): string {
  switch (level) {
    case "critical":
      return "border-l-4 border-l-red-500";
    case "high":
      return "border-l-4 border-l-red-400";
    case "elevated":
      return "border-l-4 border-l-orange-400";
    case "moderate":
      return "border-l-4 border-l-amber-400";
    case "low":
      return "border-l-4 border-l-amber-300";
    default:
      return "border-l-4 border-l-transparent";
  }
}

export interface UrgencyStyle {
  bgClassName: string;
  iconBgClassName: string;
  textClassName: string;
  badgeClassName: string;
  buttonClassName: string;
  amountClassName: string;
}

export function getUrgencyStyle(level: UrgencyLevel): UrgencyStyle {
  switch (level) {
    case "critical":
      return {
        bgClassName: "bg-red-500/5 dark:bg-red-500/8",
        iconBgClassName: "bg-red-500/10 dark:bg-red-500/15",
        textClassName: "text-red-600 dark:text-red-400",
        badgeClassName: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-400/25",
        buttonClassName: "bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white",
        amountClassName: "font-bold text-red-600 dark:text-red-400",
      };
    case "high":
      return {
        bgClassName: "bg-red-500/4 dark:bg-red-500/6",
        iconBgClassName: "bg-red-500/8 dark:bg-red-500/12",
        textClassName: "text-red-500 dark:text-red-400",
        badgeClassName: "bg-red-500/8 text-red-500 dark:text-red-400 border-red-400/20",
        buttonClassName: "",
        amountClassName: "font-semibold text-red-500 dark:text-red-400",
      };
    case "elevated":
      return {
        bgClassName: "bg-orange-500/3 dark:bg-orange-500/5",
        iconBgClassName: "bg-orange-500/10 dark:bg-orange-500/12",
        textClassName: "text-orange-600 dark:text-orange-400",
        badgeClassName: "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-400/25",
        buttonClassName: "",
        amountClassName: "font-semibold text-orange-600 dark:text-orange-400",
      };
    case "moderate":
      return {
        bgClassName: "",
        iconBgClassName: "bg-amber-500/8 dark:bg-amber-500/10",
        textClassName: "text-amber-600 dark:text-amber-400",
        badgeClassName: "bg-amber-500/8 text-amber-600 dark:text-amber-400 border-amber-400/20",
        buttonClassName: "",
        amountClassName: "font-medium",
      };
    default:
      return {
        bgClassName: "",
        iconBgClassName: "bg-muted",
        textClassName: "text-muted-foreground",
        badgeClassName: "",
        buttonClassName: "",
        amountClassName: "font-medium",
      };
  }
}

export function getNotificationBadgeUrgency(unreadCount: number, newestCreatedAt?: string | null): {
  level: HookUrgencyLevel;
  bg: string;
  text: string;
  animate: boolean;
} {
  if (unreadCount === 0) return { level: 1, bg: "bg-muted", text: "text-muted-foreground", animate: false };
  let level: HookUrgencyLevel = 1;
  if (unreadCount >= 10) level = 4;
  else if (unreadCount >= 5) level = 3;
  else if (unreadCount >= 2) level = 2;
  if (newestCreatedAt) {
    const ageMs = Date.now() - new Date(newestCreatedAt).getTime();
    const ageMinutes = ageMs / 60000;
    if (ageMinutes < 5) level = Math.min(5, level + 1) as HookUrgencyLevel;
  }
  const map: Record<HookUrgencyLevel, { bg: string; text: string; }> = {
    1: { bg: "bg-primary", text: "text-primary-foreground" },
    2: { bg: "bg-amber-500", text: "text-white" },
    3: { bg: "bg-orange-500", text: "text-white" },
    4: { bg: "bg-red-500", text: "text-white" },
    5: { bg: "bg-red-600", text: "text-white" },
  };
  return { level, ...map[level], animate: level >= 3 };
}
